import { createSerializedAsyncQueue } from "./createSerializedAsyncQueue";

const DISLIKED_JOBS_KEY = "dislikedJobs";
const DISLIKE_THRESHOLD_DAYS = 30;
const MAX_DISLIKED_JOBS = 1000;

function getDislikedJobMap(): Record<string, number> | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawDislikedJobs = window.localStorage.getItem(DISLIKED_JOBS_KEY);
    if (!rawDislikedJobs) {
      return {};
    }

    const parsed: unknown = JSON.parse(rawDislikedJobs);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }

    const removalTimestamp = Date.now() - DISLIKE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    return Object.entries(parsed).reduce<Record<string, number>>((result, [jobKey, timestamp]) => {
      if (typeof timestamp === "number" && timestamp > removalTimestamp) {
        result[jobKey] = timestamp;
      }
      return result;
    }, {});
  } catch {
    return null;
  }
}

function persistDislikedJobMap(dislikedJobs: Record<string, number>): void {
  try {
    window.localStorage.setItem(DISLIKED_JOBS_KEY, JSON.stringify(dislikedJobs));
  } catch {
    // Storage is optional; the server and in-memory state remain authoritative for this session.
  }
}

function getDislikedJobs(): Promise<string[]> {
  const dislikedJobs = getDislikedJobMap();
  return Promise.resolve(dislikedJobs === null ? [] : Object.keys(dislikedJobs));
}

const runSerialized = createSerializedAsyncQueue();

export const dislikedJobStorage = {
  getDislikedJobs,
  addDislikedJob: (jobKey: string) =>
    runSerialized(() => {
      const dislikedJobs = getDislikedJobMap();
      if (dislikedJobs === null) {
        return Promise.resolve();
      }

      dislikedJobs[jobKey] = Date.now();

      const keys = Object.keys(dislikedJobs);
      if (keys.length > MAX_DISLIKED_JOBS) {
        const oldestJobKey = keys.reduce((oldest, current) =>
          dislikedJobs[current] < dislikedJobs[oldest] ? current : oldest,
        );
        delete dislikedJobs[oldestJobKey];
      }

      persistDislikedJobMap(dislikedJobs);
      return Promise.resolve();
    }),
  removeDislikedJob: (jobKey: string) =>
    runSerialized(() => {
      const dislikedJobs = getDislikedJobMap();
      if (dislikedJobs === null) {
        return Promise.resolve();
      }

      delete dislikedJobs[jobKey];
      persistDislikedJobMap(dislikedJobs);
      return Promise.resolve();
    }),
};
